import ModalBox from "components/Modal";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import StyledButton from "components/shared/StyledButton";
import { BodyHighlight, SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import { getObjectArraysLength } from "helpers/utils";
import { useTranslation } from "react-i18next";

import type { MissingVariable } from "../utils";
import styles from "./style.module.scss";

interface IVariablesWarningModal {
    open: boolean;
    onClose: () => void;
    /**
     * Missing variables names
     */
    missingVariables: MissingVariable[];
    /**
     * If contrat has multisignature
     */
    hasMultiSignature: boolean;
    /**
     * Formatted missing variables by beneficiary
     */
    missingVariablesByBeneficiary: Record<string, MissingVariable[]> | undefined;
}

const VariablesWarningModal = ({
    open,
    onClose,
    missingVariables,
    missingVariablesByBeneficiary,
}: IVariablesWarningModal) => {
    const beneficiariesVarCount = getObjectArraysLength(missingVariablesByBeneficiary ?? {});
    const variablesNumber = missingVariables?.length + beneficiariesVarCount;
    const hasMissingVariables = !!missingVariables?.length;
    const [t] = useTranslation();

    return (
        <ModalBox
            open={open}
            onClose={onClose}
            style={{ maxWidth: "42rem" }}
            mainTitle={t("ContractValidationEdit.IncompleteVariables")}
            subTitle={t("ContractValidationEdit.IncompleteVariablesSubtitle")}
        >
            <div className={styles["variables-warning-modal"]}>
                <div className={styles["variables-warning-modal-body"]}>
                    <BodyHighlight color="warning">
                        {variablesNumber}{" "}
                        {variablesNumber > 1
                            ? t("ContractValidationEdit.VariablesNotFilledPlural")
                            : t("ContractValidationEdit.VariablesNotFilledSingular")}
                    </BodyHighlight>
                    <ScrollShadowWrapper style={{ maxHeight: "12rem" }}>
                        {!!beneficiariesVarCount && (
                            <div className={styles["variables-warning-modal-beneficiaries-wrapper"]}>
                                {missingVariablesByBeneficiary &&
                                    Object.keys(missingVariablesByBeneficiary).map((beneficiary) => {
                                        const hasMissingVariables = !!missingVariablesByBeneficiary[beneficiary].length;
                                        if (hasMissingVariables)
                                            return (
                                                <div
                                                    key={`missing-variables-${beneficiary}`}
                                                    className={styles["variables-warning-modal-beneficiary"]}
                                                >
                                                    <SmallBodyHighlight color="grey">{beneficiary}</SmallBodyHighlight>
                                                    <div
                                                        className={
                                                            styles["variables-warning-modal-beneficiary-variables"]
                                                        }
                                                    >
                                                        {missingVariablesByBeneficiary[beneficiary].map((variable) => {
                                                            return (
                                                                <div
                                                                    className={
                                                                        styles["variables-warning-modal-variable"]
                                                                    }
                                                                    key={`variable-missing-${variable}`}
                                                                >
                                                                    <SmallBody>{variable.name}</SmallBody>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                    })}
                            </div>
                        )}
                        {hasMissingVariables && (
                            <div
                                className={styles["variables-warning-modal-variables"]}
                                style={beneficiariesVarCount ? { marginTop: "1rem" } : {}}
                            >
                                <SmallBodyHighlight color="grey">
                                    {t("ContractValidationEdit.GlobalVariables")}
                                </SmallBodyHighlight>
                                <div className={styles["variables-warning-modal-variables-wrapper"]}>
                                    {missingVariables?.map((variable) => {
                                        return (
                                            <div
                                                className={styles["variables-warning-modal-variable"]}
                                                key={`variable-missing-${variable}`}
                                            >
                                                <SmallBody>{variable.name}</SmallBody>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </ScrollShadowWrapper>
                </div>
                <div>
                    <StyledButton buttonType="outlined" fullWidth onClick={onClose}>
                        {t("ContractValidationEdit.ReturnToContract")}
                    </StyledButton>
                </div>
            </div>
        </ModalBox>
    );
};

export default VariablesWarningModal;
